import { Component, OnInit } from '@angular/core';
import { createData } from "../create-object";
import { select, Store } from "@ngrx/store";
import * as fromApp from "../../../../store/app.reducer";
import * as AssignmentsActions from '../../store/assignments.actions';
import { CustomerModel } from "../../../../models/customer.model";
import { AssignmentCategoryModel } from "../../../../models/assignmentCategory.model";
import { Observable, Subject, combineLatest } from "rxjs";
import { filter, take, takeUntil, map, skip } from "rxjs/operators";
import { NavigationStateService } from 'src/app/service/navigation-state.service';
import { selectStep } from '../../store/assignment.selector';

@Component({
  selector: 'app-step1',
  templateUrl: './step1.component.html',
  styleUrls: ['./step1.component.scss'],
  standalone: false
})
export class Step1Component implements OnInit {
  public customer$: Observable<CustomerModel>;
  public categories$: Observable<AssignmentCategoryModel[]>;
  public selectedCategoryId: number | null = null;
  private destroy$ = new Subject<void>();
  loadPrevious: any;
  private hasAutoSelected = false; // Track if we've already auto-selected to prevent re-selection

  constructor(private store: Store<fromApp.AppState>,
    private navState: NavigationStateService
  ) {
    // this.store.dispatch(new AssignmentsActions.UpdateStep(1));
  }

  ngOnInit(): void {
    // Check navigation flags synchronously at the start
    const isBack = this.navState.getIsBackNavigation();
    const isInternalBack = this.navState.getIsInternalBack();

    // dispatch load actions as before...
    this.store.dispatch(new AssignmentsActions.LoadAssignmentCategories());
    this.customer$ = this.store.select(state => state.auth.customer);
    this.categories$ = this.store.select(state => state.assignments.categories);

    this.categories$
      .pipe(filter(cats => !!cats?.length), take(1))
      .subscribe(cats => {
        const storedId = localStorage.getItem('selectedCategoryId');
        if (storedId) {
          const found = cats.find(cat => cat.id === +storedId);
          if (found) this.selectedCategoryId = +storedId;
        }
      });

    // Only load previous assignment if we're NOT navigating back
    // This prevents auto-selecting category when user clicks back from step 2
    if (!isBack && !isInternalBack) {
      this.store.dispatch(
        new AssignmentsActions.LoadPreviousAssignment({
          assignment_category_id: createData.assignmentCategoryId,
          skill_id: createData.skillId,
        })
      );
    } else {
      // Reset internal back flag when navigating back
      if (isInternalBack) {
        setTimeout(() => this.navState.setInternalBack(false), 100);
      }
    }

    // Combine step and previousAssignments to ensure we only auto-select when appropriate
    const isNavigatingBack = isBack || isInternalBack;

    let observable = combineLatest([
      this.store.pipe(select(selectStep)),
      this.store.select(state => state.assignments.previousAssignments)
    ]).pipe(
      takeUntil(this.destroy$)
    );

    // If navigating back, skip the first emission (cached data)
    if (isNavigatingBack) {
      observable = observable.pipe(skip(1));
    }

    observable.pipe(
      // Only proceed if we're on step 1 and not navigating back
      filter(([currentStep, data]) => {
        const currentIsBack = this.navState.getIsBackNavigation();
        const currentIsInternalBack = this.navState.getIsInternalBack();

        // If navigating back, skip this emission entirely
        if (currentIsBack || currentIsInternalBack) {
          // Reset the flag
          if (currentIsInternalBack) {
            setTimeout(() => this.navState.setInternalBack(false), 100);
          }
          return false; // Skip this emission
        }

        // Only allow if we're on step 1 and have data
        return currentStep === 1 && !!data?.data;
      })
    )
      .subscribe(([currentStep, data]) => {
        this.loadPrevious = data?.data;

        // Only auto-select if we haven't already done so and we have valid data
        // Double-check we're still on step 1 (in case step changed)
        if (this.loadPrevious?.assignmentCategory?.id && !this.hasAutoSelected && currentStep === 1) {
          this.hasAutoSelected = true; // Mark as auto-selected to prevent re-selection
          this.onCreateFromCategory(this.loadPrevious.assignmentCategory.id);
        }
      });
  }

  // ✅ Handles category selection
  onCreateFromCategory(id: number): void {
    this.selectedCategoryId = id;
    localStorage.setItem('selectedCategoryId', id.toString());

    this.store.dispatch(
      new AssignmentsActions.CreateAssignmentFromHistory({ isFromHistory: false })
    );

    // Use Object.assign to ensure we can modify the object even if it's sealed
    // This works for sealed objects but not frozen ones
    try {
      Object.assign(createData, { assignmentCategoryId: id });
    } catch (e) {
      // If Object.assign fails (object is frozen), try defineProperty
      try {
        Object.defineProperty(createData, 'assignmentCategoryId', {
          value: id,
          writable: true,
          enumerable: true,
          configurable: true
        });
      } catch (defineError) {
        console.error('Cannot modify createData - object is not extensible:', defineError);
        // Last resort: try direct assignment (will throw error but we catch it)
        (createData as any).assignmentCategoryId = id;
      }
    }

    if (createData.assignmentCategoryId) {
      try {
        Object.assign(createData, {
          skillId: undefined,
          uniformId: undefined
        });
      } catch (e) {
        // If assignment fails, try direct property access
        try {
          (createData as any).skillId = undefined;
          (createData as any).uniformId = undefined;
        } catch (e2) {
          console.error('Cannot modify createData properties:', e2);
        }
      }
    }

    this.store.dispatch(
      new AssignmentsActions.LoadPreviousAssignment({ assignment_category_id: id })
    );

    this.store.dispatch(new AssignmentsActions.UpdateStep(2));
  }

  trackByCategoryId(index: number, category: AssignmentCategoryModel): number {
    return category.id;
  }

  ngOnDestroy() {
    this.hasAutoSelected = false; // Reset flag when component is destroyed
    this.destroy$.next();
    this.destroy$.complete();
  }
}